import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, 
  FolderOpen, 
  Calendar, 
  Users, 
  GraduationCap,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  BookOpen
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

// Programme Card Component - defined outside to avoid re-renders
const ProgrammeCard = ({ programme, isAdmin, navigate, handleDelete }) => (
  <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge 
              variant={programme.is_published ? "default" : "secondary"}
              className={programme.is_published ? "bg-teal-500/20 text-teal-400" : "bg-zinc-700 text-zinc-400"}
            >
              {programme.is_published ? "Published" : "Draft"}
            </Badge>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400">
              {programme.structure_type === 'weeks' ? 'Weekly' : 'Monthly'}
            </Badge>
          </div>
          <CardTitle className="text-lg text-white line-clamp-2">
            {programme.programme_name || 'Untitled Programme'}
          </CardTitle>
        </div>
        
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4 text-zinc-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
              <DropdownMenuItem 
                onClick={() => navigate(`/admin/programmes/${programme.programme_id}/edit`)}
                className="text-white hover:bg-zinc-800"
              >
                <Edit className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate(`/admin/programmes/${programme.programme_id}/learners`)}
                className="text-white hover:bg-zinc-800"
              >
                <Users className="h-4 w-4 mr-2" /> Manage Learners
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate(`/admin/programmes/${programme.programme_id}/gradebook`)}
                className="text-white hover:bg-zinc-800"
              >
                <BookOpen className="h-4 w-4 mr-2" /> Gradebook
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleDelete(programme.programme_id)}
                className="text-red-400 hover:bg-zinc-800"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </CardHeader>
    
    <CardContent className="space-y-4">
      {programme.description && (
        <p className="text-sm text-zinc-400 line-clamp-2">{programme.description}</p>
      )}
      
      <div className="grid grid-cols-2 gap-3 text-sm">
        {programme.professional_body && (
          <div className="flex items-center gap-2 text-zinc-400">
            <GraduationCap className="h-4 w-4 text-teal-500" />
            <span className="truncate">{programme.professional_body}</span>
          </div>
        )}
        {programme.duration && (
          <div className="flex items-center gap-2 text-zinc-400">
            <Calendar className="h-4 w-4 text-amber-500" />
            <span>{programme.duration}</span>
          </div>
        )}
        {programme.nqf_level && (
          <div className="flex items-center gap-2 text-zinc-400">
            <FolderOpen className="h-4 w-4 text-purple-500" />
            <span>NQF {programme.nqf_level}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-zinc-400">
          <Users className="h-4 w-4 text-blue-500" />
          <span>{programme.enrolled_count || 0} enrolled</span>
        </div>
      </div>
      
      <div className="pt-3 border-t border-zinc-800 flex gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          onClick={() => navigate(`/programmes/${programme.programme_id}`)}
        >
          <Eye className="h-4 w-4 mr-2" />
          View
        </Button>
        {isAdmin && (
          <Button 
            size="sm" 
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
            onClick={() => navigate(`/admin/programmes/${programme.programme_id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default function ProgrammesPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, user } = useAuth();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchProgrammes();
  }, [isAdmin]);

  const fetchProgrammes = async () => {
    try {
      // Admin sees all programmes, others see only published
      const endpoint = isAdmin ? `${API}/programmes/admin/all` : `${API}/programmes`;
      const res = await axios.get(endpoint, { headers });
      setProgrammes(res.data.programmes || []);
    } catch (error) {
      console.error('Failed to fetch programmes:', error);
      // Don't show error for public endpoint if empty
      if (isAdmin) {
        toast.error('Failed to load programmes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (programmeId) => {
    if (!window.confirm('Are you sure you want to delete this programme? This will remove all associated data.')) {
      return;
    }

    try {
      await axios.delete(`${API}/programmes/${programmeId}`, { headers });
      toast.success('Programme deleted');
      fetchProgrammes();
    } catch (error) {
      toast.error('Failed to delete programme');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-white mb-2">
              Professional Programmes
            </h1>
            <p className="text-zinc-400">
              Structured learning pathways with multiple content formats
            </p>
          </div>
          
          {isAdmin && (
            <Button 
              onClick={() => navigate('/admin/programmes/create')}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Programme
            </Button>
          )}
        </div>

        {/* Programmes Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-zinc-800 rounded w-1/4 mb-4" />
                <div className="h-6 bg-zinc-800 rounded w-3/4 mb-4" />
                <div className="h-4 bg-zinc-800 rounded w-full mb-2" />
                <div className="h-4 bg-zinc-800 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : programmes.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No programmes yet</h3>
            <p className="text-zinc-400 mb-6">
              {isAdmin 
                ? "Create your first professional programme to get started"
                : "No programmes are available at the moment"}
            </p>
            {isAdmin && (
              <Button 
                onClick={() => navigate('/admin/programmes/create')}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Programme
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programmes.map((programme) => (
              <ProgrammeCard 
                key={programme.programme_id} 
                programme={programme} 
                isAdmin={isAdmin}
                navigate={navigate}
                handleDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
